'use client';

import { Shift, ShiftTimeMapping } from '@/lib/types';
import { generateIcs, downloadIcs } from '@/lib/ics-generator';
import { defaultCaseyMappings, defaultVhhMappings } from '@/lib/shift-mappings';

interface Props {
  shifts: Shift[];
  userName: string;
  format: 'mmc' | 'casey' | 'vhh';
  customMappings: ShiftTimeMapping[];
}

export default function IcsExporter({ shifts, userName, format, customMappings }: Props) {
  const shiftCount = shifts.filter(s => s.type === 'shift').length;
  const leaveCount = shifts.filter(s => s.type === 'leave').length;
  const otherCount = shifts.filter(s => s.type === 'other').length;

  const handleExport = () => {
    const resolvedShifts = shifts.map(shift => {
      if (shift.type === 'shift' && shift.startTime) return shift;

      const allMappings = format === 'casey'
        ? [...defaultCaseyMappings, ...customMappings]
        : format === 'vhh'
        ? [...defaultVhhMappings, ...customMappings]
        : customMappings;

      const mapping = allMappings.find(m => m.code === shift.label);
      if (mapping) {
        return {
          ...shift,
          startTime: mapping.startTime,
          endTime: mapping.endTime,
          isOvernight: mapping.isOvernight,
          type: 'shift' as const,
        };
      }
      return shift;
    });
    const icsContent = generateIcs(resolvedShifts, format);
    const safeName = userName.replace(/[^a-z0-9._-]/gi, '_').toLowerCase();
    downloadIcs(icsContent, `${safeName}-roster.ics`);
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="text-sm text-zinc-600 dark:text-zinc-400">
        <p>{shiftCount} shifts, {leaveCount} leave days, {otherCount} other entries</p>
      </div>
      <button
        onClick={handleExport}
        disabled={shiftCount === 0 && leaveCount === 0 && otherCount === 0}
        className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-400 text-white rounded-lg font-medium text-lg transition-colors"
      >
        Download ICS File
      </button>
      <p className="text-xs text-zinc-500">
        Import into Google Calendar, Outlook, Apple Calendar, etc.
      </p>
    </div>
  );
}